/**
 * Engine de Diagnóstico de Usuário (FXCS-4128).
 *
 * Funções puras de normalização, regras de detecção e cálculo de score.
 * Sem dependência de Vue/Vuex para facilitar testes unitários.
 */

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type Servico = 'cadastro' | 'agendamento' | 'seguranca' | 'keycloak' | 'remuneracao'
export type TipoBusca = 'cpf' | 'id' | 'email'
export type Criticidade = 'critica' | 'alta' | 'media' | 'baixa'

export interface RegistroServico {
  servico: Servico
  status: 'encontrado' | 'nao_encontrado' | 'indisponivel'
  id?: string
  userId?: string
  cpf?: string
  nome?: string
  email?: string
  telefones?: string[]
  ativo?: boolean
  crm?: string
  especialidade?: string
  tipo?: string
  criadoEm?: string
  atualizadoEm?: string
  erro?: string
  detalhes?: DetalheServico[]
  gruposPermissao?: GrupoPermissao[]
  extras?: Record<string, unknown>
}

export interface DetalheServico {
  label: string
  valor: string
  alerta?: boolean
}

export interface GrupoPermissao {
  titulo: string
  itens: string[]
}

export interface Achado {
  regraId: string
  titulo: string
  criticidade: Criticidade
  campo: string
  valoresPorServico: Record<string, unknown>
  recomendacao: string
}

export interface Consolidado {
  chaveBusca: string
  tipoBusca: TipoBusca
  registros: RegistroServico[]
  achados: Achado[]
  score: number
  duracaoMs: number
}

// ---------------------------------------------------------------------------
// Normalização
// ---------------------------------------------------------------------------

export function normalizarCpf(s?: string | null): string {
  if (!s) return ''
  return s.replace(/\D/g, '')
}

export function normalizarEmail(s?: string | null): string {
  if (!s) return ''
  return s.trim().toLowerCase()
}

export function normalizarNome(s?: string | null): string {
  if (!s) return ''
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export function normalizarTelefone(s?: string | null): string {
  if (!s) return ''
  const d = s.replace(/\D/g, '')
  if (d.startsWith('55') && d.length > 11) return d.substring(2)
  return d
}

/** CPF é válido conforme dígitos verificadores. */
export function cpfValido(cpf: string): boolean {
  const n = normalizarCpf(cpf)
  if (n.length !== 11) return false
  if (/^(\d)\1{10}$/.test(n)) return false
  return [9, 10].every((i) => {
    let soma = 0
    for (let j = 0; j < i; j++) soma += parseInt(n[j], 10) * (i + 1 - j)
    const dv = (soma * 10) % 11
    return (dv === 10 ? 0 : dv) === parseInt(n[i], 10)
  })
}

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function guidValido(s: string): boolean {
  return GUID_REGEX.test(s.trim())
}

export function emailValido(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())
}

// ---------------------------------------------------------------------------
// Regras de detecção
// ---------------------------------------------------------------------------

export const SERVICOS: Servico[] = ['cadastro', 'agendamento', 'seguranca', 'keycloak', 'remuneracao']

export const TITULO_SERVICO: Record<Servico, string> = {
  cadastro: 'Cadastro',
  agendamento: 'Agendamento',
  seguranca: 'Segurança',
  keycloak: 'Keycloak',
  remuneracao: 'Remuneração',
}

function encontrados(c: Consolidado): RegistroServico[] {
  return c.registros.filter(r => r.status === 'encontrado')
}

function porServico(c: Consolidado): Record<Servico, RegistroServico[]> {
  return SERVICOS.reduce((acc, s) => {
    acc[s] = c.registros.filter(r => r.servico === s && r.status === 'encontrado')
    return acc
  }, {} as Record<Servico, RegistroServico[]>)
}

function chave(r: RegistroServico): string {
  return `${r.servico}/${(r.id || '?').substring(0, 8)}`
}

/** R1: pessoa órfã entre serviços */
// Remuneração só tem quem usa o financeiro — ausência lá não caracteriza órfã
const SERVICOS_PRESENCA_OBRIGATORIA: Servico[] = ['cadastro', 'agendamento', 'seguranca', 'keycloak']

function r1PessoaOrfa(c: Consolidado): Achado[] {
  const por = porServico(c)
  const presentes = SERVICOS_PRESENCA_OBRIGATORIA.filter(s => por[s].length > 0)
  const ausentes = SERVICOS_PRESENCA_OBRIGATORIA.filter(s => por[s].length === 0)
  if (presentes.length === 0 || ausentes.length === 0) return []
  return [{
    regraId: 'R1',
    titulo: 'Pessoa órfã entre serviços',
    criticidade: 'alta',
    campo: 'presenca',
    valoresPorServico: SERVICOS_PRESENCA_OBRIGATORIA.reduce((acc, s) => {
      acc[s] = por[s].length > 0 ? 'presente' : 'ausente'
      return acc
    }, {} as Record<string, string>),
    recomendacao: `Pessoa existe em ${presentes.join(', ')} mas não em ${ausentes.join(', ')}. Verificar fluxo de criação/sincronização.`,
  }]
}

/** R2: duplicidade intra-serviço (com mensagem específica se CPFs diferem) */
function r2Duplicidade(c: Consolidado): Achado[] {
  const out: Achado[] = []
  const por = porServico(c)
  SERVICOS.filter(servico => por[servico].length >= 2).forEach((servico) => {
    const regs = por[servico]
    const cpfs = new Map<string, string>()
    regs.forEach((r) => {
      const cpfN = normalizarCpf(r.cpf || '')
      if (cpfN) cpfs.set(cpfN, (r.id || '?').substring(0, 8))
    })
    if (cpfs.size > 1) {
      const valores: Record<string, string> = {}
      cpfs.forEach((rid, cpf) => { valores[`${servico}/${rid}`] = cpf })
      out.push({
        regraId: 'R2',
        titulo: `Duplicidade em ${servico} com CPFs diferentes`,
        criticidade: 'critica',
        campo: 'cpf',
        valoresPorServico: valores,
        recomendacao: `${regs.length} registros em ${servico} para a mesma pessoa com CPFs distintos. Provável CPF de teste/erro em um deles. Investigar.`,
      })
    } else {
      out.push({
        regraId: 'R2',
        titulo: `Duplicidade em ${servico}`,
        criticidade: 'critica',
        campo: 'id',
        valoresPorServico: { [servico]: regs.map(r => r.id || '?') },
        recomendacao: `${regs.length} registros distintos em ${servico} para a mesma pessoa. Avaliar merge/depreciação.`,
      })
    }
  })
  return out
}

/** R3: nome divergente */
function r3NomeDivergente(c: Consolidado): Achado[] {
  const regs = encontrados(c).filter(r => r.nome)
  if (regs.length < 2) return []
  const valores = new Set(regs.map(r => normalizarNome(r.nome)))
  if (valores.size < 2) return []
  return [{
    regraId: 'R3',
    titulo: 'Nome divergente entre serviços',
    criticidade: 'alta',
    campo: 'nome',
    valoresPorServico: regs.reduce((acc, r) => {
      acc[chave(r)] = r.nome
      return acc
    }, {} as Record<string, unknown>),
    recomendacao: 'Padronizar o nome pelo registro de Cadastro.',
  }]
}

/** R4: email divergente */
function r4EmailDivergente(c: Consolidado): Achado[] {
  const regs = encontrados(c).filter(r => r.email)
  if (regs.length < 2) return []
  const valores = new Set(regs.map(r => normalizarEmail(r.email)))
  if (valores.size < 2) return []
  return [{
    regraId: 'R4',
    titulo: 'E-mail divergente entre serviços',
    criticidade: 'alta',
    campo: 'email',
    valoresPorServico: regs.reduce((acc, r) => {
      acc[chave(r)] = r.email
      return acc
    }, {} as Record<string, unknown>),
    recomendacao: 'Verificar se há conta duplicada ou troca de e-mail não sincronizada.',
  }]
}

/** R6: status ativo divergente */
function r6StatusAtivo(c: Consolidado): Achado[] {
  const regs = encontrados(c).filter(r => typeof r.ativo === 'boolean')
  if (regs.length < 2) return []
  const valores = new Set(regs.map(r => r.ativo))
  if (valores.size < 2) return []
  return [{
    regraId: 'R6',
    titulo: 'Status ativo divergente',
    criticidade: 'media',
    campo: 'ativo',
    valoresPorServico: regs.reduce((acc, r) => {
      acc[chave(r)] = r.ativo
      return acc
    }, {} as Record<string, unknown>),
    recomendacao: 'Sincronizar o flag de ativo/inativo entre serviços.',
  }]
}

/** R7: UserId em Segurança/Agendamento sem correspondente em Keycloak */
function r7VinculoUserId(c: Consolidado): Achado[] {
  const por = porServico(c)
  const keycloakIds = new Set(por.keycloak.map(r => (r.id || '').toLowerCase()))
  const out: Achado[] = []
  const servicosComUserId: Servico[] = ['seguranca', 'agendamento']
  servicosComUserId.forEach((servico) => {
    por[servico].forEach((r) => {
      const uid = (r.userId || r.id || '').toLowerCase()
      if (uid && !keycloakIds.has(uid)) {
        out.push({
          regraId: 'R7',
          titulo: `UserId em ${servico} sem correspondente em Keycloak`,
          criticidade: 'critica',
          campo: 'userId',
          valoresPorServico: { [servico]: uid, keycloak: 'AUSENTE' },
          recomendacao: `Usuário não consegue autenticar com este UserId. Deprecar ${uid} ou recriar em Keycloak.`,
        })
      }
    })
  })
  return out
}

/** R10: cadastro fantasma (em Keycloak/Segurança sem Cadastro) */
function r10CadastroFantasma(c: Consolidado): Achado[] {
  const por = porServico(c)
  if (por.cadastro.length === 0 && (por.keycloak.length > 0 || por.seguranca.length > 0)) {
    return [{
      regraId: 'R10',
      titulo: 'Cadastro fantasma',
      criticidade: 'alta',
      campo: 'presenca',
      valoresPorServico: {
        cadastro: 'AUSENTE',
        seguranca: por.seguranca.length > 0 ? 'presente' : 'ausente',
        keycloak: por.keycloak.length > 0 ? 'presente' : 'ausente',
      },
      recomendacao: 'Usuário autenticável mas sem perfil completo de pessoa. Concluir cadastro.',
    }]
  }
  return []
}

const REGRAS = [
  r1PessoaOrfa,
  r2Duplicidade,
  r3NomeDivergente,
  r4EmailDivergente,
  r6StatusAtivo,
  r7VinculoUserId,
  r10CadastroFantasma,
]

// ---------------------------------------------------------------------------
// Score
// ---------------------------------------------------------------------------

const PESOS: Record<Criticidade, number> = {
  critica: 4,
  alta: 2,
  media: 1,
  baixa: 0.3,
}

export function calcularScore(achados: Achado[]): number {
  if (achados.length === 0) return 0
  const soma = achados.reduce((acc, a) => acc + (PESOS[a.criticidade] || 0), 0)
  return Math.min(10, Math.round(soma * 0.8 * 10) / 10)
}

// ---------------------------------------------------------------------------
// API pública: avaliar e camposDivergentes
// ---------------------------------------------------------------------------

export function avaliar(c: Consolidado): Achado[] {
  const out: Achado[] = []
  REGRAS.forEach((regra) => {
    try {
      out.push(...regra(c))
    } catch (e) {
      // uma regra com bug não pode derrubar a tela
      // eslint-disable-next-line no-console
      console.warn('Erro ao avaliar regra:', e)
    }
  })
  return out
}

export function camposDivergentes(achados: Achado[]): Set<string> {
  return new Set(achados.map(a => a.campo))
}
